import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  VStack,
  Stack,
  HStack,
  useColorModeValue,
  FormErrorMessage,
  IconButton,
} from "@chakra-ui/react";
import { useRedeemUserStampRewardMutation, useValidateRewardTypeMutation } from "../../slices/adminApiSlice";
import { useGetUserDataMutation, useGetDecryptRewardQrCodeMutation } from "../../slices/userApiSlice";
import Loader from "../../components/Loader";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { ToastService } from "../../services/toast/toastService";
import QrScanner from "react-qr-scanner";
import { FaQrcode, FaTimes } from "react-icons/fa";

const RedeemptionByStamps = () => {
  const { adminInfo } = useSelector((state) => state.adminAuth);
  const navigate = useNavigate();
  const [redeemUserStampReward, { isLoading }] = useRedeemUserStampRewardMutation();
  const [decryptData] = useGetDecryptRewardQrCodeMutation();
  const [getUserData] = useGetUserDataMutation();
  const [validateRewardType] = useValidateRewardTypeMutation();
  const [formData, setFormData] = useState({
    adminId: "",
    userId: "",
    rewardId: "",
    points: 0,
  
  });
  const [formErrors, setFormErrors] = useState({});
  const { success, error } = ToastService();
  const bgColor = useColorModeValue("white", "gray.700");
  const currentAdminId = adminInfo ? adminInfo._id : null;
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [userData, setUserData] = useState({
    current_stamps: 0,
  });
  const [isQrScannerVisible, setQrScannerVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const safeTrim = (value) => {
    return typeof value === 'string' ? value.trim() : '';  // Return empty string if not a valid string
  };
  
  const isFormValid = () => {
    return (
      safeTrim(formData.userId) !== '' &&
      formData.points > 0 &&
      userData?.current_stamps >= formData.points // Ensure current points are sufficient
    );
  };

  useEffect(() => {
    if (adminInfo) {
      setFormData((prevData) => ({
          ...prevData,
          adminId: currentAdminId, // Set adminId to currentAdminId
      }));
    }
  }, [adminInfo, currentAdminId]);

  // Using useRef to persist the timeout across renders
  const typingTimeout = useRef(null);

  const handleQrCodeButton = () => {
    setQrScannerVisible(!isQrScannerVisible);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear the previous timeout (if any)
    clearTimeout(typingTimeout.current);

    // Set a new timeout to update user data after 2 second of inactivity
    typingTimeout.current = setTimeout(async () => {
        try {
            const encryptedData  = value;
            const response = await decryptData(encryptedData).unwrap();
            
            // Check if required fields are present
            if (!response || !response.userId || !response.rewardId || !response.pointsRequired) {
                // Optionally, handle missing fields with some validation logic
                setFormErrors({ ...formErrors, userId: 'Missing required fields in QR code data.' });
                return;
            }

            const validReward = await validateRewardType(response.rewardId).unwrap();
            if(!validReward) {
              error("Reward is not valid");
              setUserData(null);
              return false; // Indicate failure
            }

            // Fetch user data
            const userResponse = await getUserData(response.userId).unwrap();

            // Check if the user has enough stamps to redeem the reward
            if (userResponse.user.current_stamps < response.pointsRequired) {
                setFormErrors({ ...formErrors, points: 'You do not have enough stamps to redeem this reward.' });
                //return; // Stop further execution
            }
            
            // Set user data and form data
            setUserData(userResponse.user);

            // Update form data state
            setFormData({
            adminId: currentAdminId,
            userId: response.userId,
            rewardId: response.rewardId,
            points: response.pointsRequired,
            });
            
        } catch (err) {
            // Handle invalid JSON format
            setFormErrors({ ...formErrors, userId: 'Invalid JSON data. Please scan the QR code again.' });
        }
    }, 1000);
  };  

  const handleNumberChange = (valueAsString, valueAsNumber, field) => {
    setFormData({ ...formData, [field]: valueAsNumber });
    setFormErrors({ ...formErrors, [field]: "" }); // Clear error on change
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      setErrorMessage('Please fill in all required fields.');
    } else {
      setErrorMessage(null); // Reset error message when form is valid
    }

    // Start loading when form submission begins
    setLoading(true);
    try {
      // Perform the asynchronous action (e.g., redeeming the reward)
      const response = await redeemUserStampReward({ ...formData }).unwrap();

      if (response.status >= 400) {
        // console.error("Error response:", response);
        error(response.message || 'Failed to redeem the stamp reward. Please try again.');
      } else {
        success(response.message);
        setFormData({ userId: "", rewardId: "", points: 0 });

        // Reset the current points to 0
        setUserData(prevData => ({ ...prevData, current_stamps: 0 }));

      }
    } catch (err) {
      // Handle any errors during the async operation
      error(err.data?.message || "An unexpected error occurred.");
    } finally {
      // Always set loading to false when the operation completes
      setLoading(false);
    }
  };

  const handleError = (err) => {
    // console.error(err);
    setErrorMessage("Failed to scan. Please try again.");
  };

  const handleScan = async (data) => {
    if (!data) return;
  
    // setIsScanning(true);
    try {
      const encryptedData  = data.text;

      const response = await decryptData(encryptedData).unwrap();
          
      // Check if required fields are present
      if (!response || !response.userId || !response.rewardId || !response.pointsRequired) {
        // Optionally, handle missing fields with some validation logic
        setFormErrors({ ...formErrors, userId: 'Missing required fields in QR code data.' });
        return;
      }

      await validateRewardType(response.rewardId).unwrap();
  
      // Fetch user data
      const userResponse = await getUserData(response.userId).unwrap();

      // Check if the user has enough stamps to redeem the reward
      if (userResponse.user.current_stamps < response.pointsRequired) {
        setFormErrors({ ...formErrors, points: 'You do not have enough stamps to redeem this reward.' });
        //return; // Stop further execution
      }

      // Set user data and form data
      setUserData(userResponse.user);
      setFormData({
        adminId: currentAdminId,
        userId: response.userId,
        rewardId: response.rewardId,
        points: response.pointsRequired,
      });
  
      success("Scan QR has been successfully.");
      setIsReadOnly(true);
      setErrorMessage('');
      
    } catch (error) {
      // console.error("Error during scan:", error);
      const errorMessage = error.data?.message || error.message || "An unexpected error occurred.";
      setErrorMessage(errorMessage);
    }
    setQrScannerVisible(false); // Optionally hide the QR scanner after scan
  };  

  return (
    <>
      <Stack 
        w={'full'}
        bg={bgColor}
        spacing={6} 
        maxW={"full"} 
        rounded={"lg"} 
        boxShadow={'2xl'} 
        p={8} 
        borderWidth={'thin'}
        >
        <form onSubmit={handleSubmit}>
          {isLoading ? (
            <Loader />
          ) : (
            <VStack spacing={2} align="stretch">
              <Stack
                justify="center" 
                align="center"
                >

                {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
                
                {/* Add Toggle Button for QR Scanner */}
                <IconButton
                    icon={isQrScannerVisible ? <FaTimes /> : <FaQrcode />}
                    onClick={handleQrCodeButton}
                    aria-label="Toggle QR Scanner"
                    colorScheme="teal"
                    variant={'outline'}
                    width="full"
                    mt={4}
                    _hover={{ bg: 'teal.500', color: 'white' }}
                  />

                  {/* Conditionally render the QR scanner */}
                  {isQrScannerVisible && (
                    <Box mt={4} borderWidth={1} borderRadius="md" p={4}>
                        <QrScanner 
                          id="qr-scanner"
                          //facingMode="environment"  // Request back camera
                          onError={handleError}
                          onScan={(data) => {
                            handleScan(data);
                          }}
                          delay={300}
                        />
                    </Box>
                  )}
              </Stack>
              <HStack align="start">
                {/* Reward QR Code */}
                <Box w="full">
                    <FormControl isRequired isInvalid={!!formErrors.userId} minH="auto">
                    <FormLabel>Reward QR Code</FormLabel>
                    <Input 
                        name="userId" 
                        placeholder="User ID" 
                        value={formData.userId} 
                        onChange={handleChange} 
                        readOnly={isReadOnly} 
                        autoFocus
                    />
                    <FormErrorMessage>{formErrors.userId}</FormErrorMessage>
                    </FormControl>
                </Box>

                {/* Reward ID */}
                <Box w="full">
                    <FormControl isRequired isInvalid={!!formErrors.rewardId} minH="auto">
                    <FormLabel>Reward ID</FormLabel>
                    <Input 
                        name="rewardId" 
                        placeholder="Reward ID" 
                        value={formData.rewardId} 
                        onChange={handleChange} 
                        readOnly={isReadOnly} 
                    />
                    <FormErrorMessage>{formErrors.rewardId}</FormErrorMessage>
                    </FormControl>
                </Box>
                </HStack>

                <HStack align="start">
                {/* Points Required */}
                <Box w="full">
                    <FormControl isRequired minH="auto">
                    <FormLabel>Stamps Required</FormLabel>
                    <NumberInput
                        min={1}
                        readOnly={isReadOnly}
                        value={formData.points}
                        onChange={(valueStr, valueNum) => {
                        if (valueStr.length <= 3) {
                            handleNumberChange(valueStr, valueNum, "points");
                        }
                        }}
                    >
                        <NumberInputField name="points" />
                    </NumberInput>
                    </FormControl>
                </Box>

                {/* Current Points */}
                <Box w="full">
                    <FormControl isRequired isInvalid={!!formErrors.points || userData?.current_stamps < formData.points} minH="auto">
                    <FormLabel>Current Stamps</FormLabel>
                    <NumberInput
                        min={1}
                        readOnly={isReadOnly}
                        value={userData?.current_stamps}
                        defaultValue={'0'}
                        style={{
                        backgroundColor: userData?.current_stamps < formData.points ? 'lightcoral' : 'initial'
                        }}
                    >
                        <NumberInputField name="currentStamps" />
                    </NumberInput>
                    <FormErrorMessage>{formErrors.points}</FormErrorMessage>
                    </FormControl>
                </Box>
                </HStack>

              <Stack spacing={2} direction={['column', 'row']}>

              {/* Submit Button with loading state */}
              <Button
                type="submit"
                colorScheme="teal"
                width="full"
                mt={4}
                isDisabled={!isFormValid() || loading}  // Disable if form is invalid or if loading
                isLoading={loading}  // Show loading spinner when submitting
                
              >
                Redeem
              </Button>
                <Button 
                  width="full" 
                  mt={4}
                  onClick={() => navigate(-1)} 
                  colorScheme="gray" 
                  >
                  Cancel
                </Button>
              </Stack>
            </VStack>
          )}
        </form>  
      </Stack>
    </>
  );
};

export default RedeemptionByStamps;